import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { useFirebase } from './FirebaseContext';
import { formatDate } from '../hooks/DateTime';
import { ProviderData, EventData, BlogData } from '../types/firebase';



interface FirebaseData {
  eventsData: EventData[];
  providerData: ProviderData[];
  blogsData: BlogData[];
  loading: boolean;
  error: Error | null;
}

const DataContext = createContext<FirebaseData | null>(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState<FirebaseData>({
    eventsData: [],
    providerData: [],
    blogsData: [],
    loading: true,
    error: null
  });
  
  const {app}= useFirebase();

  useEffect(() => {
    const fetchFirebaseData = async () => {
      try {
        const db = getFirestore(app);
        const events = collection(db, 'events');
        const providers = collection(db, 'providers');
        const blogs = collection(db, 'blogs');
        
        const [eventsSnapshot, providersSnapshot, blogsSnapshot] = await Promise.all([
          getDocs(events),
          getDocs(providers),
          getDocs(blogs)
        ]);

        const eventsData = eventsSnapshot.docs.map(doc => ({
          ...(doc.data() as EventData)
        }));
        const providerData = providersSnapshot.docs.map(doc => ({
          ...(doc.data() as ProviderData)
        }));

        // Combine the data
        const combinedData = eventsData.map(event => ({
          ...event,
          date: formatDate(event.date),
          providerDetails: providerData.find(
            provider => provider.provider_name === event.provider_name
          ) || null,
          // Ensure all required fields are present with default values if necessary
          event_name: event.event_name || '',
          event_description: event.event_description || '',
          start_time: event.start_time || '',
          event_link: event.event_link || '',
          event_types: event.event_types || [],
          end_time: event.end_time || '',
          address: event.address || '',
        }));

        const blogsData = blogsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...(doc.data() as BlogData)
        }));

        setData({
          eventsData: combinedData,
          providerData,
          blogsData,
          loading: false,
          error: null
        });
      } catch (err) {
        setData(prev => ({
          ...prev,
          loading: false,
          error: err as Error
        }));
      }
    };

    fetchFirebaseData();
  }, [app]);

  return (
    <DataContext.Provider value={data}>
      {children}
    </DataContext.Provider>
  );
};